import { USER_ROLES, VEHICLE_TYPES, VEHICLE_TYPES_LABEL } from '../../../constants/constants';
import { vehicleOptionsDictionary } from '../../../dictionaries';

export const getOrganizationName = (ride, healthOrganizations = []) =>
  ride?.healthOrganization?.name ||
  healthOrganizations.find((org) => org.id === ride?.healthOrganizationId)?.name ||
  'N/A';

export const getTransportCompanyName = (ride, transportCompanies = []) =>
  ride?.transportCompany?.name ||
  transportCompanies.find((company) => company.id === ride?.transportCompanyId)?.name ||
  'N/A';

export const statusLabel = {
  PENDING: 'Pending',
  ACCEPTED: 'Accepted',
  START: 'Start',
  STARTED: 'Started',
  COMPLETED: 'Completed',
  EXPIRED: 'Expired',
  CANCELED: 'Canceled',
  UNFULFILLED: 'Unfulfilled',
  UNABLE_TO_FULFILL: 'Unable to Fulfill',
  IN_PROGRESS: 'In Progress',
  RESCHEDULED: 'Rescheduled',
};

export const detailedViewStatusPillColors = {
  COMPLETED: '#408E63',
  UNABLE_TO_FULFILL: '#636363',
  EXPIRED: '#EF4444',
  IN_PROGRESS: '#2F90F2',
  UNFULFILLED: '#EF4444',
  CANCELED: '#EF4444',
};

export const detailedViewStatusPillDefaultColor = '#334155';

const detailedViewVehicleTypeDisplay = {
  [VEHICLE_TYPES.AMBULATORY_RIDER_IS_WALKING]: { primary: 'Ambulatory' },
  [VEHICLE_TYPES.AMBULATORY_ASSIST_RIDER_IS_WALKING]: { primary: 'Ambulatory Assist' },
  [VEHICLE_TYPES.UBER_AMBULATORY_RIDE]: { primary: 'Uber Health', isUberHealth: true },
  [VEHICLE_TYPES.STRETCHER_BOUND_BARIATRIC]: {
    primary: 'Stretcher Bound',
    secondary: 'Bariatric (over 250 lbs)',
  },
  [VEHICLE_TYPES.STRETCHER_BOUND_NON_BARIATRIC]: {
    primary: 'Stretcher Bound',
    secondary: 'Non-Bariatric',
  },
  [VEHICLE_TYPES.WHEEL_CHAIR_BOUND_BARIATRIC]: {
    primary: 'Wheelchair Bound',
    secondary: 'Bariatric (over 250 lbs)',
  },
  [VEHICLE_TYPES.WHEEL_CHAIR_BOUND_NON_BARIATRIC]: {
    primary: 'Wheelchair Bound',
    secondary: 'Non-Bariatric',
  },
};

export function getDetailedViewStatusLabel(status) {
  return statusLabel[status] ?? status ?? 'N/A';
}

export function getDetailedViewStatusColor(status) {
  return detailedViewStatusPillColors[status] ?? detailedViewStatusPillDefaultColor;
}

export function getDetailedViewOrganizationLines(displayName) {
  if (displayName == null || displayName === '') {
    return ['N/A'];
  }
  const s = String(displayName).trim();
  if (!s) return ['N/A'];

  const words = s.split(/\s+/);
  if (words.length > 2) {
    return [words.slice(0, 2).join(' '), words.slice(2).join(' ')];
  }

  if (
    words.length === 2 &&
    /^org$/i.test(words[0]) &&
    /^[0-9a-f-]{20,}$/i.test(words[1])
  ) {
    const u = words[1];
    const g = u.match(
      /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4})(-[0-9a-f-]+)$/i
    );
    if (g) {
      return [`Org ${g[1]}`, g[2].replace(/^-/, '')];
    }
  }

  return [s];
}

export function getDetailedViewFirstTwoWordsLines(displayName) {
  if (displayName == null || displayName === '') {
    return ['N/A'];
  }
  const s = String(displayName).trim();
  if (!s) return ['N/A'];
  const words = s.split(/\s+/);
  if (words.length > 2) {
    return [words.slice(0, 2).join(' '), words.slice(2).join(' ')];
  }
  return [s];
}

export function getDetailedViewVehicleTypeDisplay(vehicleType) {
  if (
    vehicleType === VEHICLE_TYPES.UBER_AMBULATORY_RIDE ||
    vehicleType === 'Uber: Ambulatory, curb-to-curb via Uber Health'
  ) {
    return detailedViewVehicleTypeDisplay[VEHICLE_TYPES.UBER_AMBULATORY_RIDE];
  }
  if (detailedViewVehicleTypeDisplay[vehicleType]) {
    return detailedViewVehicleTypeDisplay[vehicleType];
  }
  const label = VEHICLE_TYPES_LABEL[vehicleType] || vehicleType || 'N/A';
  return { primary: label };
}

export function getDetailedViewVehicleTypeFilterLabel(vehicleType) {
  const display = getDetailedViewVehicleTypeDisplay(vehicleType);
  if (display.secondary) {
    return `${display.primary} - ${display.secondary}`;
  }
  return display.primary;
}

function getViewDashboardActualRole(viewDashboardMode) {
  if (viewDashboardMode?.actualRole) {
    return viewDashboardMode.actualRole;
  }

  try {
    const stored = sessionStorage.getItem('viewDashboardMode');
    return stored ? JSON.parse(stored).actualRole : null;
  } catch {
    return null;
  }
}

function isInsightsEnabledForUser(user) {
  if (!user) return false;

  return Boolean(
    user.organization?.addons?.insightsEnabled ||
    user.orgAddons?.insightsEnabled
  );
}

export function isHoAdminInsightsView({ authorizationRole, viewDashboardMode, user }) {
  if (authorizationRole === USER_ROLES.HO_ADMIN) {
    return true;
  }

  if (isInsightsEnabledForUser(user)) {
    return true;
  }

  if (!viewDashboardMode?.active) {
    return false;
  }

  const actualRole = getViewDashboardActualRole(viewDashboardMode);

  if (actualRole === USER_ROLES.TC_ADMIN) {
    return false;
  }

  if (
    actualRole === USER_ROLES.HO_ADMIN ||
    actualRole === USER_ROLES.ENTERPRISE_ADMIN
  ) {
    return true;
  }

  return Boolean(user?.onBehalfOfRoleId && authorizationRole !== USER_ROLES.TC_ADMIN);
}

export function getDetailedViewVehicleTypeFullLabel(vehicleType) {
  if (!vehicleType) return 'N/A';
  if (vehicleOptionsDictionary[vehicleType]) {
    return vehicleOptionsDictionary[vehicleType];
  }
  if (vehicleType === 'Uber: Ambulatory, curb-to-curb via Uber Health') {
    return vehicleType;
  }
  return getDetailedViewVehicleTypeFilterLabel(vehicleType);
}

export function getDetailedViewVehicleTypeSideInfo(vehicleType) {
  const dictionaryLabel =
    vehicleOptionsDictionary[vehicleType] ||
    (vehicleType === 'Uber: Ambulatory, curb-to-curb via Uber Health' ? vehicleType : null);

  if (!dictionaryLabel) return null;

  const display = getDetailedViewVehicleTypeDisplay(vehicleType);

  if (display.isUberHealth) {
    return dictionaryLabel.replace(/^Uber:\s*/, '');
  }

  const pillSummary = display.secondary
    ? `${display.primary} - ${display.secondary}`
    : display.primary;

  if (dictionaryLabel === pillSummary) {
    return null;
  }

  const primaryPrefix = `${display.primary} - `;
  if (dictionaryLabel.startsWith(primaryPrefix)) {
    return dictionaryLabel.slice(primaryPrefix.length);
  }

  return dictionaryLabel;
}
